import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Stack,
  Typography,
  Checkbox,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EventIcon from '@mui/icons-material/Event';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ChecklistIcon from '@mui/icons-material/Checklist';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import {
  getSubtasks,
  createSubtasks,
  updateSubtask,
  toggleSubtaskStatus,
  deleteSubtask,
  clearSubtasks,
} from '../../../redux/features/taskManagementSlice';

const fmt = (d) => (d && dayjs(d).isValid() ? dayjs(d).format('DD MMM YYYY') : '');
const isOverdue = (d, done) => !done && d && dayjs(d).isValid() && dayjs(d).isBefore(dayjs(), 'day');

const TaskSubtasks = ({ taskId }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { subtasks, subtasksLoading } = useSelector((state) => state.taskManagement);

  const [loadedFor, setLoadedFor] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [busyId, setBusyId] = useState(null);
  const addInputRef = useRef(null);

  const load = () => {
    if (!taskId) return;
    dispatch(getSubtasks(taskId)).finally(() => setLoadedFor(taskId));
  };

  useEffect(() => {
    dispatch(clearSubtasks());
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const ready = loadedFor === taskId;
  const list = Array.isArray(subtasks) ? subtasks : [];
  const completedCount = list.filter((s) => s.is_completed === 1).length;
  const progress = list.length > 0 ? Math.round((completedCount / list.length) * 100) : 0;

  // Quick-add stays open and re-focused after every submit so several
  // subtasks can be entered back-to-back without re-clicking anything.
  const handleAdd = () => {
    const title = newTitle.trim();
    if (!title) return;
    setSubmitting(true);
    dispatch(createSubtasks({ task_id: taskId, subtasks: [{ title }] })).then((res) => {
      setSubmitting(false);
      if (res?.payload?.status === 'success') {
        setNewTitle('');
        addInputRef.current?.focus();
      } else {
        enqueueSnackbar(res?.payload?.message || 'Failed to add subtask', { variant: 'error' });
      }
    });
  };

  const startEdit = (s) => {
    setEditingId(s.subtask_id);
    setEditTitle(s.title);
    setEditDueDate(s.due_date ? dayjs(s.due_date).format('YYYY-MM-DD') : '');
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = (s) => {
    const title = editTitle.trim();
    if (!title) {
      enqueueSnackbar('Title cannot be empty', { variant: 'warning' });
      return;
    }
    setBusyId(s.subtask_id);
    dispatch(updateSubtask({ subtask_id: s.subtask_id, title, due_date: editDueDate || null })).then((res) => {
      setBusyId(null);
      if (res?.payload?.status === 'success') {
        setEditingId(null);
      } else {
        enqueueSnackbar(res?.payload?.message || 'Failed to update subtask', { variant: 'error' });
      }
    });
  };

  const handleToggle = (s) => {
    // UI already flips instantly (optimistic update lives in the slice) - this
    // just fires the request and surfaces an error if the server disagrees.
    dispatch(toggleSubtaskStatus({ subtask_id: s.subtask_id, is_completed: s.is_completed !== 1 })).then((res) => {
      if (res?.payload?.status !== 'success') {
        enqueueSnackbar(res?.payload?.message || 'Failed to update subtask', { variant: 'error' });
      }
    });
  };

  const handleDelete = (s) => {
    setBusyId(s.subtask_id);
    dispatch(deleteSubtask(s.subtask_id)).then((res) => {
      setBusyId(null);
      if (res?.payload?.status !== 'success') {
        enqueueSnackbar(res?.payload?.message || 'Failed to delete subtask', { variant: 'error' });
        load(); // the optimistic removal needs undoing - resync with the server
      }
    });
  };

  return (
    <Box>
      {/* Progress */}
      {list.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748B' }}>
              {completedCount} of {list.length} completed
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#6366F1' }}>{progress}%</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: '#EEF2F6',
              '& .MuiLinearProgress-bar': { borderRadius: 3, background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' },
            }}
          />
        </Box>
      )}

      {/* Always-visible quick add - no click-to-reveal step */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <TextField
          inputRef={addInputRef}
          placeholder="Add a subtask and press Enter..."
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          fullWidth
          size="small"
          disabled={submitting}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
        />
        <Tooltip title="Add subtask">
          <span>
            <IconButton
              onClick={handleAdd}
              disabled={submitting || !newTitle.trim()}
              sx={{
                color: '#fff',
                background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                '&:hover': { background: 'linear-gradient(135deg, #5558E6, #7C4FE0)' },
                '&.Mui-disabled': { background: '#E2E8F0', color: '#94A3B8' },
              }}
            >
              {submitting ? <CircularProgress size={18} color="inherit" /> : <AddIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Box
        sx={{
          maxHeight: 320,
          overflowY: 'auto',
          pr: 0.5,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { background: '#E2E8F0', borderRadius: 3 },
        }}
      >
        {!ready ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
            <CircularProgress size={26} />
          </Stack>
        ) : list.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 4, color: '#94A3B8' }}>
            <ChecklistIcon sx={{ fontSize: 36, color: '#CBD5E1' }} />
            <Typography sx={{ fontSize: '0.85rem' }}>Break this task down into smaller steps.</Typography>
          </Stack>
        ) : (
          <Stack spacing={1}>
            {list.map((s) => {
              const editing = editingId === s.subtask_id;
              const done = s.is_completed === 1;
              const busy = busyId === s.subtask_id;
              const overdue = isOverdue(s.due_date, done);
              return (
                <Stack
                  key={s.subtask_id}
                  direction="row"
                  alignItems="flex-start"
                  spacing={1}
                  sx={{
                    border: '1px solid',
                    borderColor: overdue ? '#FCA5A5' : '#EEF2F6',
                    borderRadius: 2.5,
                    px: 1,
                    py: 0.5,
                    background: done ? '#F8FAFC' : '#fff',
                    opacity: busy ? 0.6 : 1,
                    transition: 'opacity 0.15s ease, background 0.15s ease',
                  }}
                >
                  <Checkbox
                    checked={done}
                    onChange={() => handleToggle(s)}
                    size="small"
                    sx={{ mt: 0.25, color: '#94A3B8', '&.Mui-checked': { color: '#6366F1' } }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0, py: 0.75 }}>
                    {editing ? (
                      <Stack spacing={1}>
                        <TextField
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveEdit(s);
                            } else if (e.key === 'Escape') {
                              cancelEdit();
                            }
                          }}
                          fullWidth
                          size="small"
                          autoFocus
                        />
                        <TextField
                          type="date"
                          value={editDueDate}
                          onChange={(e) => setEditDueDate(e.target.value)}
                          size="small"
                          label="Due date"
                          InputLabelProps={{ shrink: true }}
                          sx={{ maxWidth: 180 }}
                        />
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            startIcon={<CloseIcon sx={{ fontSize: 15 }} />}
                            onClick={cancelEdit}
                            sx={{ textTransform: 'none', color: '#64748B' }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={busy}
                            startIcon={<CheckIcon sx={{ fontSize: 15 }} />}
                            onClick={() => saveEdit(s)}
                            sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }}
                          >
                            Save
                          </Button>
                        </Stack>
                      </Stack>
                    ) : (
                      <>
                        <Typography
                          sx={{
                            fontSize: '0.85rem',
                            color: done ? '#94A3B8' : '#334155',
                            textDecoration: done ? 'line-through' : 'none',
                            wordBreak: 'break-word',
                          }}
                        >
                          {s.title}
                        </Typography>
                        {s.due_date && (
                          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ mt: 0.25 }}>
                            <EventIcon sx={{ fontSize: 13, color: overdue ? '#EF4444' : '#94A3B8' }} />
                            <Typography sx={{ fontSize: '0.72rem', color: overdue ? '#EF4444' : '#94A3B8', fontWeight: overdue ? 700 : 400 }}>
                              {overdue ? `Overdue · ${fmt(s.due_date)}` : `Due ${fmt(s.due_date)}`}
                            </Typography>
                          </Stack>
                        )}
                      </>
                    )}
                  </Box>
                  {!editing && (
                    <Stack direction="row" spacing={0.25} sx={{ pt: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" disabled={busy} onClick={() => startEdit(s)} sx={{ color: '#64748B' }}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" disabled={busy} onClick={() => handleDelete(s)} sx={{ color: '#EF4444' }}>
                          {busy ? <CircularProgress size={13} color="error" /> : <DeleteOutlineIcon sx={{ fontSize: 15 }} />}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default TaskSubtasks;
